import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from '@emotion/styled';
import {
  SIDE_PANEL_MIN_CONTAINER_WIDTH,
  SIDE_PANEL_TRANSITION_EASING,
  SIDE_PANEL_TRANSITION_MS,
} from '@trinity/chat-components/components/Chat.SidePanel/sizing';
import { MainAreaRightShiftContext, SidePanelAnimatedContext } from './context';

type Props = {
  children: React.ReactNode;
  themeVersion?: 'v1' | 'v2';
  isPanelOpen?: boolean;
};

// Slack on top of the animation duration before the width transition is disarmed, so a
// busy frame between applying the width and the browser starting the animation can't cut
// the animation short.
const TRANSITION_DISARM_BUFFER_MS = 100;

export const SidePanelLayout = ({ children, themeVersion = 'v1', isPanelOpen }: Props) => {
  return (
    <Root data-panel-layout-container themeVersion={themeVersion} isPanelOpen={isPanelOpen}>
      {children}
    </Root>
  );
};

const Root = styled('div')<Pick<Props, 'themeVersion' | 'isPanelOpen'>>(({ themeVersion, isPanelOpen }) => ({
  display: 'flex',
  alignItems: 'stretch',
  minWidth: 0,
  width: '100%',
  gap: themeVersion === 'v2' && isPanelOpen ? '15px' : undefined,
  background:
    themeVersion === 'v2'
      ? 'linear-gradient(352deg, rgba(236, 234, 240, 1) 0%, rgba(249, 249, 251, 1) 69%)'
      : undefined,
}));

type MainAreaProps = {
  isOpen?: boolean;
  /**
   * Whether this box is a scroll container for the page inside it (the default). Pages built to
   * scroll with the window should pass `false`: an `overflow: auto` box would stop their
   * `position: sticky` elements sticking to the viewport and can put a scrollbar of its own inside
   * the layout.
   */
  scrolls?: boolean;
  children?: React.ReactNode;
};

// Measures how far its own right edge sits from the viewport's right edge and provides
// that shift via context (see `MainAreaRightShiftContext`) — this box already reflects
// exactly the space available for main content: flex:1 against the side panel's flex:0,
// so the shift is 0 whenever nothing is eating into that space and grows only when (and
// by exactly as much as) the panel actually opens, unaffected by whatever centering or
// padding an app applies to its own content further down. Consumers like
// `useToolPaneAnchor` read this instead of measuring their own nested container, and add
// it on top of their own stylesheet's base gutter (e.g. `right: calc(10px + var(--x))`)
// so a floating pane doesn't end up back against the raw viewport edge — on top of the
// side panel — once one can render there.
export const MainArea = ({ isOpen, scrolls = true, children }: MainAreaProps) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [rightShift, setRightShift] = useState(0);

  useLayoutEffect(() => {
    if (!element) return undefined;

    const measure = () => {
      const rect = element.getBoundingClientRect();
      const viewportWidth = element.ownerDocument.documentElement.clientWidth;
      setRightShift(Math.max(0, viewportWidth - rect.right));
    };

    // Set initial value immediately; don't wait for the observer tick.
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element]);

  return (
    <StyledMainArea ref={setElement} isOpen={isOpen} scrolls={scrolls}>
      <MainAreaRightShiftContext.Provider value={rightShift}>{children}</MainAreaRightShiftContext.Provider>
    </StyledMainArea>
  );
};

// By default this box is a scrollport, so it is the offset origin for every `position: sticky`
// descendant — they must anchor to `--sticky-offset` (0 here) rather than `--navbar-height`,
// which this box's top edge already sits below. Without `scrolls` it is a plain box and the page
// inside keeps scrolling with the window, so its sticky offsets are untouched.
const StyledMainArea = styled('div', { shouldForwardProp: (prop) => prop !== 'scrolls' })<{
  isOpen?: boolean;
  scrolls: boolean;
}>(({ scrolls }) => ({
  flex: 1,
  minWidth: 0,
  ...(scrolls && {
    overflow: 'auto',
    '--sticky-offset': '0px',
  }),
}));

type SidePanelAreaProps = {
  isOpen: boolean;
  panelTop?: string;
  panelHeight?: string;
  children?: React.ReactNode;
  themeVersion?: 'v1' | 'v2';
  // Skip the slide when `isOpen` flips, for closes the user didn't ask for (e.g. the route can't host the panel).
  instant?: boolean;
};

// Animated shell for the side panel.
//
// The panel owns its own (drag-resizable) pixel width, so this shell can't hard-code the
// value it animates to — it measures the rendered content instead and drives its own width
// between 0 and that measurement. Because `MainArea` is `flex: 1` against this box, every
// frame of that width transition squeezes/releases the main content by exactly the same
// amount, so both sides move together without a second animation to keep in sync.
//
// The content is absolutely pinned to the right edge and clipped by `overflow: hidden`, so
// a collapsing width reads as the panel sliding off the right edge rather than as its
// contents being crushed.
export const SidePanelArea = ({
  isOpen,
  panelTop,
  panelHeight,
  children,
  themeVersion = 'v1',
  instant = false,
}: SidePanelAreaProps) => {
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  // Last non-zero measurement. Frozen while closed so the collapse has a width to travel
  // from even after the panel unmounts its content at the end of the animation.
  const [contentWidth, setContentWidth] = useState(0);

  useLayoutEffect(() => {
    if (!content) return undefined;

    const measure = () => {
      const width = content.getBoundingClientRect().width;
      if (width > 0) {
        setContentWidth(width);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(content);

    return () => {
      observer.disconnect();
    };
  }, [content]);

  // The transition is armed only around an open/close toggle. While the panel is at rest
  // its width still tracks the measurement (drag-resize, viewport clamping), and animating
  // those would make the resize handle rubber-band behind the cursor.
  const previousIsOpenRef = useRef(isOpen);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Covers the render in which `isOpen` flipped: the effect below hasn't run yet, but the
  // transition must already be on the element or the first width change won't animate.
  const isTogglingOpenState = previousIsOpenRef.current !== isOpen;
  const targetWidth = isOpen ? contentWidth : 0;

  useEffect(() => {
    if (previousIsOpenRef.current === isOpen) return undefined;
    previousIsOpenRef.current = isOpen;
    setIsTransitioning(true);
    return undefined;
  }, [isOpen]);

  // Disarm once the animation has had time to run — but time it from the width the element
  // actually animates to, not from the toggle. On the very first open the panel has no
  // measurement yet, so it opens at width 0 and only reaches its real width once the chat
  // tree has mounted and the observer has reported back. During app start-up that can take
  // longer than the animation itself, and timing from the toggle would disarm the
  // transition before the width it is supposed to animate ever arrives.
  useEffect(() => {
    if (!isTransitioning) return undefined;

    const timeoutId = window.setTimeout(
      () => setIsTransitioning(false),
      SIDE_PANEL_TRANSITION_MS + TRANSITION_DISARM_BUFFER_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [isTransitioning, targetWidth]);

  return (
    <SidePanelViewport
      isOpen={isOpen}
      panelTop={panelTop}
      panelHeight={panelHeight}
      shouldAnimate={!instant && (isTogglingOpenState || isTransitioning)}
      style={{ width: targetWidth }}
      themeVersion={themeVersion}
    >
      <SidePanelContent ref={setContent} themeVersion={themeVersion}>
        <SidePanelAnimatedContext.Provider value={true}>{children}</SidePanelAnimatedContext.Provider>
      </SidePanelContent>
    </SidePanelViewport>
  );
};

type SidePanelViewportProps = {
  isOpen: boolean;
  panelTop?: string;
  panelHeight?: string;
  shouldAnimate: boolean;
  themeVersion?: 'v1' | 'v2';
};

const SidePanelViewport = styled('div')<SidePanelViewportProps>(
  ({ isOpen, panelTop, panelHeight, shouldAnimate, themeVersion }) => ({
    // Flexbox layout guarantee: this panel respects the main content area and does not overlap.
    // flexShrink: 0 ensures the panel takes only the space it needs; MainArea (flex: 1) shrinks accordingly.
    // The sticky positioning is for vertical alignment only and does not affect horizontal layout.
    // Keep the side panel in layout flow so opening it shifts the main content,
    // but pin it below the navbar so header/actions stay visible while scrolling.
    position: 'sticky',
    top: panelTop ?? 'var(--navbar-height, 60px)',
    height: panelHeight ?? 'calc(100vh - var(--navbar-height, 60px))',
    display: 'flex',
    flexShrink: 0, // Critical: prevents shrinking, preserves panel width
    alignSelf: 'flex-start',
    alignItems: 'stretch',
    padding: 0,
    background: themeVersion === 'v2' ? 'transparent' : '#FFFFFF',
    // Clip while animating (and whenever closed) so the width transition reads as the panel
    // sliding in/out. At rest while open, stay visible: the measurement of a drag-resize
    // lands a frame after the drag itself, and clipping that gap makes the panel's edge
    // stutter behind the cursor.
    overflow: isOpen && !shouldAnimate ? 'visible' : 'hidden',
    // Prevent wheel/trackpad scroll from chaining into the main page.
    overscrollBehavior: 'contain',
    pointerEvents: isOpen ? 'auto' : 'none',
    transition: shouldAnimate
      ? `width ${SIDE_PANEL_TRANSITION_MS}ms ${SIDE_PANEL_TRANSITION_EASING}`
      : 'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    // Mirrors the JS fallback threshold: below the width that can host the panel, the modal
    // takes over, so the panel must never render. Using the shared constant guarantees there
    // is no dead zone where the panel is "open" but hidden.
    [`@media (max-width: ${SIDE_PANEL_MIN_CONTAINER_WIDTH - 1}px)`]: { display: 'none' },
  })
);

// Pinned to the right edge so it keeps its full width while the viewport around it
// collapses — the panel travels off-screen instead of being squashed. `max-content` keeps
// the measurement tied to the panel's own width rather than to the shrinking viewport it
// is positioned against.
const SidePanelContent = styled('div')<Pick<SidePanelViewportProps, 'themeVersion'>>(({ themeVersion }) => ({
  position: 'absolute',
  top: themeVersion === 'v2' ? '15px' : '0px',
  right: themeVersion === 'v2' ? '15px' : '0px',
  height: themeVersion === 'v2' ? 'calc(100% - 30px)' : '100%',
  width: 'max-content',
  display: 'flex',
  alignItems: 'stretch',
}));
